import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import platformMessagesService from '../../services/platformMessagesService';
import PlatformMessagesTable from './PlatformMessagesTable';
import PlatformMessageFormModal from './PlatformMessageFormModal';

const AdminPlatformMessagesPage = () => {
  const { t } = useTranslation();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);

  const loadCountries = useCallback(async () => {
    try {
      setCountriesLoading(true);
      const list = await platformMessagesService.listCountriesForPicker();
      setCountries(list);
    } catch (e) {
      console.error(e);
      toast.error(t('adminPlatformMessages.countriesLoadError'));
      setCountries([]);
    } finally {
      setCountriesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await platformMessagesService.listAll();
      setMessages(list);
    } catch (e) {
      console.error(e);
      toast.error(t('adminPlatformMessages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const breadcrumbItems = [
    { label: t('sidebar.nav.dashboard'), path: '/dashboard', icon: 'LayoutDashboard' },
    {
      label: t('sidebar.nav.administration.platformMessages'),
      path: '/admin/platform-messages',
      icon: 'Megaphone',
    },
  ];

  const handleDelete = async (msg) => {
    if (!window.confirm(t('adminPlatformMessages.deleteConfirm', { title: msg.title }))) {
      return;
    }
    try {
      await platformMessagesService.remove(msg.id);
      toast.success(t('adminPlatformMessages.deleteSuccess'));
      load();
    } catch (e) {
      console.error(e);
      toast.error(t('adminPlatformMessages.deleteError'));
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-4 py-6 space-y-6 max-w-5xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">
                  {t('sidebar.nav.administration.platformMessages')}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {t('adminPlatformMessages.pageDescription')}
                </p>
              </div>
              <Button
                onClick={() => {
                  setEditing(null);
                  setModalOpen(true);
                }}
              >
                {t('adminPlatformMessages.createButton')}
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <PlatformMessagesTable
                messages={messages}
                loading={loading}
                onEdit={(msg) => {
                  setEditing(msg);
                  setModalOpen(true);
                }}
                onDelete={handleDelete}
              />
            </div>
          </div>
        </main>
      </div>

      <PlatformMessageFormModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        message={editing}
        onSaved={load}
        countries={countries}
        countriesLoading={countriesLoading}
      />
    </div>
  );
};

export default AdminPlatformMessagesPage;
